const MINIMUM_RADIUS_PX = 80;
const MARKER_GAP_PX = 16;
const START_ANGLE_RADIANS = -Math.PI / 2;
export const SPIDERFY_MARKER_Z_INDEX = 2 ** 31 - 1;

export interface IMapMarkerOffset {
  x: number;
  y: number;
}

export function hasSpiderfyOffset(offset?: IMapMarkerOffset) {
  return offset !== undefined && (offset.x !== 0 || offset.y !== 0);
}

interface ISpiderfyMarkerSize {
  width: number;
}

export interface ISpiderfyMarker {
  setMap(map: google.maps.Map | null): void;
}

export interface ISpiderfyClusterMarker extends ISpiderfyMarker {
  setVisible(visible: boolean): void;
}

export interface ISpiderfyCluster {
  marker?: ISpiderfyClusterMarker;
  markers: Array<ISpiderfyMarker>;
}

interface IRegisteredSpiderfyMarker {
  data: object;
  width: number;
}

interface ISpiderfyControllerOptions {
  resolveMarker(marker: ISpiderfyMarker): IRegisteredSpiderfyMarker | undefined;
  setOffset(data: object, offset: IMapMarkerOffset | undefined): void;
}

interface IActiveSpiderfy {
  clusterMarker: ISpiderfyClusterMarker;
  map: google.maps.Map;
  markers: Array<{ data: object; marker: ISpiderfyMarker }>;
}

interface IHandleClusterClickOptions {
  currentZoom: number | undefined;
  maximumZoom: number;
  openNativeCluster(): void;
  openSpiderfy(): void;
}

function normalizeRoundedPixel(value: number) {
  const roundedValue = Math.round(value);
  return Object.is(roundedValue, -0) ? 0 : roundedValue;
}

export function calculateSpiderfyOffsets(markerSizes: Array<ISpiderfyMarkerSize>): Array<IMapMarkerOffset> {
  if (markerSizes.length === 0) {
    return [];
  }

  const widestMarker = Math.max(...markerSizes.map(({ width }) => width));
  const adjacentMarkerDistance = widestMarker + MARKER_GAP_PX;
  const radiusForMarkerWidth =
    markerSizes.length === 1 ? 0 : adjacentMarkerDistance / (2 * Math.sin(Math.PI / markerSizes.length));
  const radius = Math.max(MINIMUM_RADIUS_PX, radiusForMarkerWidth);

  return markerSizes.map((_, index) => {
    const angle = START_ANGLE_RADIANS + (index * 2 * Math.PI) / markerSizes.length;
    return {
      x: normalizeRoundedPixel(Math.cos(angle) * radius),
      y: normalizeRoundedPixel(Math.sin(angle) * radius),
    };
  });
}

export function createSpiderfyController(options: ISpiderfyControllerOptions) {
  let activeSpiderfy: IActiveSpiderfy | undefined;

  function clearOffsets(spiderfy: IActiveSpiderfy) {
    spiderfy.markers.forEach(({ data }) => options.setOffset(data, undefined));
  }

  function resolveClusterMarkers(markers: Array<ISpiderfyMarker>) {
    const resolvedMarkers: Array<IRegisteredSpiderfyMarker & { marker: ISpiderfyMarker }> = [];
    for (const marker of markers) {
      const registration = options.resolveMarker(marker);
      if (!registration) {
        return undefined;
      }
      resolvedMarkers.push({ ...registration, marker });
    }
    return resolvedMarkers;
  }

  function close() {
    const spiderfy = activeSpiderfy;
    if (!spiderfy) {
      return;
    }

    clearOffsets(spiderfy);
    spiderfy.markers.forEach(({ marker }) => {
      marker.setMap(null);
    });
    spiderfy.clusterMarker.setVisible(true);
    spiderfy.clusterMarker.setMap(spiderfy.map);
    activeSpiderfy = undefined;
  }

  function discard() {
    const spiderfy = activeSpiderfy;
    if (!spiderfy) {
      return;
    }

    clearOffsets(spiderfy);
    activeSpiderfy = undefined;
  }

  function open(cluster: ISpiderfyCluster, map: google.maps.Map) {
    if (!cluster.marker) {
      return false;
    }

    const resolvedMarkers = resolveClusterMarkers(cluster.markers);
    if (!resolvedMarkers) {
      return false;
    }

    close();

    const offsets = calculateSpiderfyOffsets(resolvedMarkers);
    cluster.marker.setMap(null);
    resolvedMarkers.forEach(({ data, marker }, index) => {
      options.setOffset(data, offsets[index]);
      marker.setMap(map);
    });
    activeSpiderfy = {
      clusterMarker: cluster.marker,
      map,
      markers: resolvedMarkers.map(({ data, marker }) => ({ data, marker })),
    };
    return true;
  }

  function matches(cluster: ISpiderfyCluster) {
    if (!activeSpiderfy || cluster.markers.length !== activeSpiderfy.markers.length) {
      return false;
    }

    const activeMarkers = new Set(activeSpiderfy.markers.map(({ marker }) => marker));
    return cluster.markers.every((marker) => activeMarkers.has(marker));
  }

  function refresh(cluster: ISpiderfyCluster, map: google.maps.Map) {
    const spiderfy = activeSpiderfy;
    if (!spiderfy || !cluster.marker || !matches(cluster)) {
      return false;
    }

    spiderfy.clusterMarker = cluster.marker;
    spiderfy.map = map;
    cluster.marker.setMap(null);
    spiderfy.markers.forEach(({ marker }) => marker.setMap(map));
    return true;
  }

  return { close, discard, matches, open, refresh };
}

export function handleClusterClick(options: IHandleClusterClickOptions) {
  if ((options.currentZoom ?? Number.NEGATIVE_INFINITY) < options.maximumZoom) {
    options.openNativeCluster();
    return;
  }

  options.openSpiderfy();
}
